import type {
  DraftWarning,
  InviteDraft,
  VisitorType,
} from "@gately/validators";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useBlocker,
  useNavigate,
} from "@tanstack/react-router";
import { z } from "zod/v4";

import { Button } from "@gately/ui/button";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";
import { Logo } from "@gately/ui/logo";
import { toast } from "@gately/ui/toast";
import {
  formatDailyWindow,
  getTerms,
  hhmmToMinutes,
  inviteTypesFor,
  minutesToHhmm,
  visitorTypesByOrgType,
  wallClockOf,
} from "@gately/validators";

import type { InviteShape } from "~/components/invite/InviteShapeControl";
import type { PickableVisitor } from "~/components/invite/VisitorInputModes";
import type { VisitType, Weekday } from "~/lib/invite-validity";
import type {
  EditableField,
  ShareableGuest,
  VisitorRow,
  WindowIssue,
} from "~/lib/visitor-rows";
import { FormRenderer } from "~/components/forms/FormRenderer";
import { ImportResult } from "~/components/invite/import/ImportResult";
import { InviteShapeControl } from "~/components/invite/InviteShapeControl";
import { InviteSharePanel } from "~/components/invite/InviteSharePanel";
import {
  PassWindowFields,
  typeLabelFor,
} from "~/components/invite/PassWindowFields";
import {
  RESIDENCE,
  SharedInviteSettings,
  useInviteSharedData,
} from "~/components/invite/SharedInviteSettings";
import { VisitorInputModes } from "~/components/invite/VisitorInputModes";
import { VisitorReviewTable } from "~/components/invite/VisitorReviewTable";
import { VisitorTypeCard } from "~/components/invite/VisitorTypeCard";
import { MAX_IMPORT_ROWS } from "~/lib/invite-import";
import {
  buildValidity,
  previewValidity,
  WEEKDAYS,
} from "~/lib/invite-validity";
import { bookingLockedFields } from "~/lib/inviter-booking";
import { resolveInviterMode } from "~/lib/inviter-mode";
import { orpc, orpcClient } from "~/lib/orpc";
import { frenchErrorMessage } from "~/lib/orpc-error";
import { toWallClock } from "~/lib/row-window";
import { unitOptionLabel } from "~/lib/unit-option-label";
import { resolvePermissions } from "~/lib/use-permissions";
import { activeOrgType } from "~/lib/use-terms";
import {
  afterGroupedCreate,
  awaitsEmailSent,
  bulkSelfFill,
  canGroup,
  clearAllFormAnswers,
  commonRowWindow,
  draftGuestToVisitorRow,
  dropDateErrors,
  emptyVisitorRow,
  fillRowWindows,
  formsToCompleteLine,
  hasDateErrors,
  hasUnsavedVisitors,
  isBlankRow,
  previewChips,
  recomputeFieldErrors,
  reindexAfterPartialFailure,
  rowDisplayName,
  selfFillNotice,
  separateWindowIssue,
  setRowSelfFill,
  settingsConflict,
  submitBlocker,
  toBatchRowPayload,
  toGuestPayload,
  visitorsLine,
  withFreshClientErrors,
} from "~/lib/visitor-rows";

// Separate shape (`invite.createMany`) caps at 200 rows
// (`inviteCreateManyInput.rows`) - the same ceiling the spreadsheet import
// already enforces client-side. Grouped's own MAX_GROUPED_GUESTS (20) is
// enforced by `canGroup`/`InviteShapeControl`, not here: a list longer than
// 20 simply can't be grouped, it can still be typed and sent separately.
const MAX_ROWS = MAX_IMPORT_ROWS;
/** Rows are addressed by index: anything that re-indexes them waits for
 *  in-flight form uploads (see `uploadsInFlightRef`). */
const UPLOAD_IN_FLIGHT_MESSAGE =
  "Attendez la fin du téléversement du document.";
const MAX_EMAIL_POLL_ATTEMPTS = 3;

const inviterSearchSchema = z.object({
  guestName: z.string().max(120).optional().catch(undefined),
  guestPhone: z.string().max(32).optional().catch(undefined),
  guestEmail: z.string().max(254).optional().catch(undefined),
  /** `?copilot=1` from the /invitations split button - open the panel. */
  copilot: z
    .union([z.literal(true), z.literal("1")])
    .optional()
    .catch(undefined),
  /** Which input mode to open in - see `resolveInviterMode`. Falls back to
   *  "type" (rather than 404ing) for a residence, or when a hand-typed URL
   *  asks for a mode this org can't offer. */
  mode: z.enum(["type", "paste", "import"]).optional().catch(undefined),
  /** The booking these visitors are invited to (« Inviter des visiteurs »
   *  from a booking). The server then owns the window, type, unit and
   *  meeting point, so the page hides those controls and shows the booking's
   *  slot instead; the visit is always one grouped pass (`invite.create` —
   *  `createMany` refuses a booking). */
  bookingId: z.string().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/_dashboard/inviter/")({
  // « Nouvelle invitation » from /repertoire lands here with the visitor's
  // name and contact.
  validateSearch: (s) => inviterSearchSchema.parse(s),
  component: InviterPage,
});

/** Pre-fills the first row from `/repertoire`'s « Nouvelle invitation »
 *  action. Returns null when nothing usable was passed, so the caller keeps
 *  its default empty row. */
function seedRowFromSearch(search: {
  guestName?: string;
  guestPhone?: string;
  guestEmail?: string;
}): VisitorRow | null {
  const name = search.guestName?.trim() ?? "";
  const phone = search.guestPhone?.trim() || null;
  const email = search.guestEmail?.trim().toLowerCase() || null;
  if (!name && !phone && !email) return null;
  return { ...emptyVisitorRow(), name, phone, email };
}

function sameVisitorContact(a: VisitorRow, b: VisitorRow): boolean {
  if (a.phone && b.phone) return a.phone === b.phone;
  if (a.email && b.email) return a.email === b.email;
  return !a.phone && !b.phone && !a.email && !b.email && a.name === b.name;
}

/** Drops a picked visitor into the row list: the first empty row takes it,
 *  otherwise a new row is appended (never past `max`). Picking the same
 *  visitor twice is a no-op — one person is one guest. Adapted from the old
 *  `applyPickedVisitor` (formerly lib/invite-prefill.ts, deleted once this
 *  page absorbed its logic) to the unified `VisitorRow` (no per-row country
 *  - see `draftGuestToVisitorRow`). Only the candidate row is ever
 *  fresh-checked — an untouched row already in the list (which may carry
 *  import-tagged errors) is never re-processed. */
function applyPickedVisitorRow(
  rows: VisitorRow[],
  visitor: PickableVisitor,
  max: number,
): VisitorRow[] {
  const candidate: VisitorRow = withFreshClientErrors({
    ...emptyVisitorRow(),
    name: visitor.name,
    phone: visitor.phone,
    email: visitor.email,
  });
  if (rows.some((r) => sameVisitorContact(r, candidate))) return rows;
  const emptyIndex = rows.findIndex(isBlankRow);
  if (emptyIndex >= 0) {
    return rows.map((r, i) =>
      i === emptyIndex ? setRowSelfFill(candidate, r.selfFill) : r,
    );
  }
  if (rows.length >= max) return rows;
  return [...rows, candidate];
}

/** The server refused for the form itself: required and missing, or not
 *  the form it holds. */
function isFormRefusal(err: unknown): boolean {
  const reason =
    typeof err === "object" && err !== null
      ? (err as { data?: { reason?: unknown } | null }).data?.reason
      : undefined;
  return reason === "FORM_REQUIRED" || reason === "FORM_INVALID";
}

/** `frenchErrorMessage`, naming the visitor a form error is about: the
 *  server's FORM_INVALID message is the form's own and says nothing of whose
 *  it is (FORM_REQUIRED already names them, so no second prefix). */
function inviteErrorMessage(err: unknown, fallback: string): string {
  const message = frenchErrorMessage(err, fallback);
  const data =
    typeof err === "object" && err !== null
      ? (err as { data?: { guestName?: unknown } | null }).data
      : undefined;
  const name = typeof data?.guestName === "string" ? data.guestName.trim() : "";
  return name && !message.includes(name) ? `« ${name} » : ${message}` : message;
}

// ─── Dark preview card ────────────────────────────────────────────────────────
function PassPreview({
  guestName,
  peopleLabel,
  headline,
  unitLabel,
  unitFallback,
  type,
  typeLabel,
  start,
  end,
  orgTimeZone,
  weekdaysLabel,
  hoursLabel,
  validityLabel,
  chips,
}: {
  guestName: string;
  /** « Personnes » column: the first visitor plus their companions. */
  peopleLabel: string;
  /** Top-right label: « Invitation », « Une visite · N QR » or
   *  « N invitations · 1 QR chacune ». */
  headline: string;
  unitLabel: string;
  /** Vertical-aware placeholder shown before a unit is picked. */
  unitFallback: string;
  type: VisitType;
  /** Vertical-aware word for `type` ("Séjour" / "Multiple" for date_range). */
  typeLabel: string;
  start: Date | undefined;
  end: Date | undefined;
  /** The venue's clock — undefined only while `me` is still loading. */
  orgTimeZone: string | undefined;
  weekdaysLabel?: string;
  hoursLabel?: string;
  /** Replaces the computed « Valable » line (a booking invite's slot). */
  validityLabel?: string;
  /** One chip per visitor (`previewChips`) — shown from two visitors. */
  chips?: { chips: string[]; more: number };
}) {
  const displayName = guestName.trim() || "Nom de l'invité";
  const displayUnit = unitLabel || unitFallback;
  const validity =
    validityLabel ?? previewValidity(type, start, end, orgTimeZone);

  return (
    <div className="max-w-md min-w-0 xl:sticky xl:top-6 xl:max-w-none">
      <div className="mb-3 font-mono text-[10px] tracking-[0.1em] text-neutral-400 uppercase">
        Aperçu de l'invitation
      </div>

      {/* Dark card */}
      <div className="relative overflow-hidden rounded-[18px] bg-neutral-950 p-6">
        {/* Radial glow */}
        <div
          className="pointer-events-none absolute -top-28 -right-14 size-70 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(247,91,30,0.18), rgba(247,91,30,0) 68%)",
          }}
        />

        {/* Top bar */}
        <div className="relative flex items-center justify-between">
          <Logo variant="light" withWordmark />
          <span className="font-mono text-[10px] tracking-[0.1em] text-neutral-500 uppercase">
            {headline}
          </span>
        </div>

        {/* QR placeholder */}
        <div className="mt-5 flex items-center justify-center rounded-2xl bg-white p-4">
          <div className="flex size-45 flex-col items-center justify-center gap-2 text-center text-neutral-300">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <path d="M14 14h3v3M21 21v.01M17 21h.01M21 17h.01" />
            </svg>
            <span className="text-[11px] text-neutral-400">
              QR généré après création
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="relative mt-4">
          <div className="text-[17px] font-semibold tracking-[-0.01em] text-neutral-50">
            {displayName}
          </div>
          <div className="mt-0.5 font-mono text-[12px] text-neutral-500">
            {displayUnit} · {typeLabel}
          </div>
          <div className="mt-3.5 flex gap-6">
            <div>
              <div className="font-mono text-[10px] tracking-[0.08em] text-neutral-600 uppercase">
                Valable
              </div>
              <div className="mt-1 text-[13px] font-medium text-neutral-50">
                {validity}
              </div>
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.08em] text-neutral-600 uppercase">
                Personnes
              </div>
              <div className="mt-1 text-[13px] font-medium text-neutral-50">
                {peopleLabel}
              </div>
            </div>
            {type === "recurring" &&
              (weekdaysLabel != null || hoursLabel != null) && (
                <div>
                  <div className="font-mono text-[10px] tracking-[0.08em] text-neutral-600 uppercase">
                    Jours · heures
                  </div>
                  <div className="mt-1 text-[13px] font-medium text-neutral-50">
                    {[weekdaysLabel, hoursLabel].filter(Boolean).join(" · ") ||
                      "—"}
                  </div>
                </div>
              )}
            {/* TODO: Portail column (gates subsystem not built) */}
          </div>
          {chips && chips.chips.length >= 2 ? (
            <ul
              aria-label="Visiteurs"
              className="mt-3.5 flex flex-wrap gap-1.5"
            >
              {chips.chips.map((c, i) => (
                <li
                  key={i}
                  className="rounded-full bg-neutral-800 px-2.5 py-0.5 text-[11.5px] text-neutral-200"
                >
                  {c}
                </li>
              ))}
              {chips.more > 0 ? (
                <li className="rounded-full px-1 py-0.5 text-[11.5px] text-neutral-500">
                  +{chips.more} autres
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      </div>

      {/* Info notice */}
      <div className="mt-3.5 flex items-start gap-2.5 rounded-[10px] border border-[#FDC5A6] bg-[#FFF4EE] p-3">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#BC3A0E"
          strokeWidth="1.85"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="mt-px shrink-0"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8h.01M11 12h1v4h1" />
        </svg>
        <span className="text-[12.5px] leading-relaxed text-[#BC3A0E]">
          Le QR n'est actif que pendant la plage de validité. En dehors, l'accès
          est refusé au portail.
        </span>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
function InviterPage() {
  const navigate = useNavigate();
  // Route-scoped, typed against `inviterSearchSchema` — used only to flip
  // `?mode` without touching any other search param (guestName/copilot/etc).
  const setModeSearch = Route.useNavigate();
  const queryClient = useQueryClient();
  const refetchActiveForm = () =>
    void queryClient.invalidateQueries({
      queryKey: orpc.invite.activeForm.key(),
    });

  // People this host has already received (favourites first, then most
  // recent) — one click drops one into the first empty row (Saisir only).
  const { data: recentVisitors } = useQuery(orpc.visitor.mine.queryOptions());
  const { data: me } = useQuery(orpc.me.get.queryOptions());
  // Undefined while `me` loads — every branch below treats that as "not yet",
  // never as a role.
  const activeRole = me?.memberships.find(
    (m) => m.orgId === me.activeOrganizationId,
  )?.role;
  const isSyndic = activeRole === "syndic";
  // The venue's clock, which is what the hours the syndic picks below MEAN.
  // Undefined while `me` loads — `handleSubmit` refuses to build a validity
  // window without it rather than falling back to this browser's zone, which
  // is how a pass authored abroad used to open an hour off (GATELY-62).
  const orgTimeZone = me?.activeOrg?.timezone;
  // The org's « Fin de journée » (settings): what a one_time pass authored
  // without an end will actually get server-side, so show it as the default.
  const dayEndHhmm = minutesToHhmm(me?.activeOrg?.dayEndMinutes ?? 1439);
  // Office orgs don't have tenants — `locataire` is residential-only.
  const orgType = activeOrgType(me) ?? "residence";
  const visitorTypeOptions = visitorTypesByOrgType[orgType];
  const terms = getTerms(orgType);
  const isOffice = orgType === "office";
  const hasLlm = me?.llmAvailable ?? false;

  const search = Route.useSearch();
  const mode = resolveInviterMode(search, { isOffice, hasLlm });

  // A booking invite: the booking's slot is the window, its unit the host.
  const bookingId = search.bookingId;
  const bookingQuery = useQuery({
    ...orpc.booking.get.queryOptions({ input: { id: bookingId ?? "" } }),
    enabled: !!bookingId,
  });
  const booking = bookingId ? bookingQuery.data : undefined;
  const bookingLocked = booking ? bookingLockedFields(booking) : null;
  // Only a confirmed booking can carry visitors — the server refuses the
  // rest, so say it here rather than after the author has typed a list.
  const bookingUnusable =
    !!bookingId &&
    (bookingQuery.isError || (!!booking && booking.status !== "confirmed"));
  const bookingReady = !bookingId || booking?.status === "confirmed";

  const [rows, setRows] = useState<VisitorRow[]>(() => {
    const seeded = seedRowFromSearch(search);
    // A blank row is not an error: only a seeded one is checked.
    return [seeded ? withFreshClientErrors(seeded) : emptyVisitorRow()];
  });
  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  // Server-side failure reasons from the last `createMany` attempt, keyed by
  // ROW index. Deliberately NOT merged into `row.errors` (that array gates
  // submission) — a reason living there would make the row un-retryable,
  // the opposite of what the retry button is for. Separate shape only.
  const [failedReasons, setFailedReasons] = useState<Map<number, string>>(
    new Map(),
  );
  const [shapeChoice, setShape] = useState<InviteShape>("grouped");
  const [batchResult, setBatchResult] = useState<{
    created: number;
    failed: number;
  } | null>(null);

  const [unitId, setUnitId] = useState("");
  // The pass has a host: a picked unit, or — for a booking invite — the
  // booking's own unit, which the server fills in.
  const hasHost = bookingId ? bookingReady : !!unitId;
  const [locationId, setLocationId] = useState("");
  const [type, setType] = useState<VisitType>("one_time");
  const [start, setStart] = useState<Date | undefined>(undefined);
  const [end, setEnd] = useState<Date | undefined>(undefined);
  const [dayStart, setDayStart] = useState("");
  const [dayEnd, setDayEnd] = useState("");
  const [weekdays, setWeekdays] = useState<Set<Weekday>>(new Set());
  const [visitorType, setVisitorType] = useState<VisitorType>("visiteur");
  // A one-off office "visiteur" is always a single day; an office
  // "intervenant" doing multi-day work still needs Séjour/Récurrent so they
  // can come and go without being cut off after one scan.
  const typeOptions = inviteTypesFor(orgType, visitorType);
  const [note, setNote] = useState("");
  // The row whose form panel is open under it (two rows or more).
  const [openFormRow, setOpenFormRow] = useState<number | null>(null);
  // The separate-mode fill bar's wall clocks ("YYYY-MM-DDTHH:mm").
  const [fillFrom, setFillFrom] = useState("");
  const [fillTo, setFillTo] = useState("");

  // The host / location pickers and the visitor type's form — see
  // SharedInviteSettings. Each row holds its own answers.
  const settings = useInviteSharedData({
    visitorType,
    unitId,
    onUnitIdChange: setUnitId,
    isSyndic: activeRole === undefined ? undefined : isSyndic,
    onLocationIdChange: setLocationId,
  });
  const {
    formSchema,
    validateAnswers,
    answerErrors,
    units,
    activeFormTitle,
    formBlocker,
    uploadFile: rawUploadFile,
  } = settings;

  // Rows are addressed by index and each form's upload callback closes over
  // its row's index: deleting or replacing rows while an upload is in flight
  // would land the finished upload on another visitor's dossier. Until rows
  // get stable ids, deletes and replaces wait for uploads to finish. The ref
  // is the guard (always current, even from a stale closure such as an
  // import's onSuccess); the state re-renders the disabled delete buttons.
  // Submit, « créer une autre », the visitor-type change, paste and import
  // wait too.
  const uploadsInFlightRef = useRef(0);
  const [uploadsInFlight, setUploadsInFlight] = useState(0);
  const uploadFile = useCallback<typeof rawUploadFile>(
    async (file, meta) => {
      uploadsInFlightRef.current += 1;
      setUploadsInFlight(uploadsInFlightRef.current);
      try {
        return await rawUploadFile(file, meta);
      } finally {
        uploadsInFlightRef.current -= 1;
        setUploadsInFlight(uploadsInFlightRef.current);
      }
    },
    [rawUploadFile],
  );
  /** True (and toasts) while an upload is in flight — the guard every
   *  row-reindexing action checks first. */
  function refuseWhileUploading(): boolean {
    if (uploadsInFlightRef.current === 0) return false;
    toast.error(UPLOAD_IN_FLIGHT_MESSAGE);
    return true;
  }

  // The post-create share panel: one card per visitor, whichever path
  // created them (spec 2026-09-26 §3).
  const [shared, setShared] = useState<{
    title: string;
    subtitle: string;
    guests: ShareableGuest[];
    back: { label: string; to: string };
    secondaryBack?: { label: string; to: string };
  } | null>(null);
  const canManage = me ? resolvePermissions(me).canManage : false;
  const listBack = canManage
    ? { label: "Voir mes invitations", to: "/invitations" }
    : { label: "Voir mes invitations", to: "/attendus" };
  const [draftWarnings, setDraftWarnings] = useState<DraftWarning[]>([]);

  /** False (with a toast) while an upload is in flight. */
  function resetForm(): boolean {
    if (refuseWhileUploading()) return false;
    setRows([emptyVisitorRow()]);
    setExcluded(new Set());
    setFailedReasons(new Map());
    setShape("grouped");
    setBatchResult(null);
    setUnitId("");
    setLocationId("");
    setType("one_time");
    setStart(undefined);
    setEnd(undefined);
    setDayStart("");
    setDayEnd("");
    setWeekdays(new Set());
    setVisitorType("visiteur");
    setNote("");
    setOpenFormRow(null);
    setFillFrom("");
    setFillTo("");
    setDraftWarnings([]);
    return true;
  }

  function handleVisitorTypeChange(next: VisitorType) {
    if (next === visitorType) return;
    // The finished upload would write the OLD schema's answer back onto a
    // row whose answers this change just cleared.
    if (refuseWhileUploading()) return;
    const hasAnswers = rows.some((r) => Object.keys(r.formAnswers).length > 0);
    if (
      hasAnswers &&
      !window.confirm(
        "Changer de type efface les réponses de formulaire déjà saisies. Continuer ?",
      )
    )
      return;
    applyVisitorType(next);
  }

  /** The change itself, once confirmed — or straight from a paste, whose
   *  freshly replaced rows hold no answers yet (`rows` in this closure is
   *  still the list the paste is replacing, so asking would be wrong). */
  function applyVisitorType(next: VisitorType) {
    if (next === visitorType) return;
    setVisitorType(next);
    setRows((prev) => clearAllFormAnswers(prev));
    setOpenFormRow(null);
    // Drop a selected type the new visitor type no longer offers a selector
    // for (office visiteur: only "Unique"), so a stale choice can't submit.
    if (!inviteTypesFor(orgType, next).includes(type)) setType("one_time");
  }

  // ── Row mutations - rows are authoritative once they exist (Task 6/7
  // constraint). Every path below either edits `rows` in place or REPLACES
  // it wholesale from an explicit, already-confirmed action; none of them
  // silently discards a row a reviewer has touched.
  function handleFieldChange(
    index: number,
    field: EditableField,
    value: string,
  ) {
    if (failedReasons.has(index)) {
      setFailedReasons((prev) => {
        const next = new Map(prev);
        next.delete(index);
        return next;
      });
    }
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const next: VisitorRow = { ...r };
        switch (field) {
          case "name":
            next.name = value;
            break;
          case "phone":
            next.phone = value === "" ? null : value;
            break;
          case "email":
            next.email = value === "" ? null : value;
            break;
          case "note":
            next.note = value === "" ? null : value;
            break;
          case "validFrom":
            next.validFrom = value === "" ? null : value;
            break;
          case "validTo":
            next.validTo = value === "" ? null : value;
            break;
        }
        // Per-field only — see recomputeFieldErrors. This is the one path
        // that runs on every keystroke, for a row of ANY origin (typed,
        // pasted, or imported), so it must never touch another field's tag.
        return recomputeFieldErrors(next, field);
      }),
    );
  }

  function handleSelfFillChange(index: number, on: boolean) {
    if (failedReasons.has(index)) {
      setFailedReasons((prev) => {
        const next = new Map(prev);
        next.delete(index);
        return next;
      });
    }
    // Per-field recompute of name/phone/email only — see setRowSelfFill.
    setRows((prev) =>
      prev.map((r, i) => (i === index ? setRowSelfFill(r, on) : r)),
    );
  }

  function handleCompanionsChange(index: number, n: number) {
    setRows((prev) =>
      prev.map((r, i) =>
        i === index
          ? // Drops an import's « Accompagnants illisible » (per-field).
            recomputeFieldErrors(
              { ...r, companions: Math.max(0, Math.min(49, n)) },
              "companions",
            )
          : r,
      ),
    );
  }

  function handleFormAnswersChange(
    index: number,
    answers: Record<string, unknown>,
  ) {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, formAnswers: answers } : r)),
    );
  }

  /** Drops a row; every index-keyed piece of state after it shifts up. The
   *  list never goes empty — the last row out leaves a blank one. */
  function deleteRow(index: number) {
    if (uploadsInFlightRef.current > 0) return;
    const remap = (i: number) => (i > index ? i - 1 : i);
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [emptyVisitorRow()];
    });
    setExcluded(
      (prev) => new Set([...prev].filter((i) => i !== index).map(remap)),
    );
    setFailedReasons(
      (prev) =>
        new Map(
          [...prev].filter(([i]) => i !== index).map(([i, r]) => [remap(i), r]),
        ),
    );
    setOpenFormRow((cur) =>
      cur === null || cur === index ? null : remap(cur),
    );
  }

  function toggleRow(index: number) {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  function addRow() {
    setRows((prev) =>
      // A blank row is not an error, so it is not checked.
      prev.length >= MAX_ROWS ? prev : [...prev, emptyVisitorRow()],
    );
  }

  function pickVisitor(visitor: PickableVisitor) {
    // `applyPickedVisitorRow` already fresh-checks the candidate row itself
    // — nothing else here needs (or may safely get) a blanket recheck, since
    // an untouched row already in the list could carry import-tagged errors.
    setRows((prev) => applyPickedVisitorRow(prev, visitor, MAX_ROWS));
  }

  /** Replaces the row list wholesale, TRUSTING `next` AS-IS — used by the
   *  paste and import modes once THEY have already confirmed the
   *  replacement with the author (see VisitorInputModes). Row indices are
   *  meaningless across a replace, so exclusions and failure reasons reset
   *  with it.
   *
   *  Deliberately does NOT run `withFreshClientErrors` here: the import
   *  path's rows already carry the server's own field-tagged verdict
   *  (`VisitorInputModes`' `draftRowToVisitorRow`), including cases where a
   *  bad phone/email was parsed down to `null` — a blanket client recheck
   *  would find nothing wrong with a null field and silently erase that
   *  error. The paste path has no such verdict to protect, so it fresh-checks
   *  its OWN rows before calling this (see `applyPasteDraft`). */
  function replaceRows(next: VisitorRow[]): boolean {
    if (refuseWhileUploading()) return false;
    setRows(next);
    setExcluded(new Set());
    setFailedReasons(new Map());
    setBatchResult(null);
    setOpenFormRow(null);
    return true;
  }

  /**
   * Applies an extracted paste draft. Returns false when the extraction found
   * nobody: the most likely everyday failure is a paste with no recognizable
   * names, and wiping the rows for it would leave the user with nothing and
   * no explanation. On that branch the row list is untouched and the caller
   * keeps the panel and pasted text so they can edit and retry.
   */
  function applyPasteDraft(
    draft: InviteDraft,
    warnings: DraftWarning[],
  ): boolean {
    if (draft.guests.length === 0) {
      setDraftWarnings([
        ...warnings,
        {
          code: "no_guests",
          detail: "Aucun invité n'a été détecté dans ce texte.",
        },
      ]);
      return false;
    }
    // Paste never gets a server-side verdict on its guests (unlike import),
    // so — unlike `replaceRows`'s other caller — these rows DO need a fresh
    // client check before they're shown as clean.
    // Refused while a form upload is in flight (replaceRows toasts why):
    // false keeps the pasted text so the author can retry.
    if (
      !replaceRows(
        draft.guests.map(draftGuestToVisitorRow).map(withFreshClientErrors),
      )
    )
      return false;
    // Same path as a manual change (spec 2026-09-26 §3), minus the confirm:
    // the rows it would clear were just replaced.
    if (draft.visitorType) applyVisitorType(draft.visitorType);
    // Ignore a copilot-guessed type the vertical + visitor type combo
    // doesn't expose a selector for (office visiteur: always "Unique")
    // rather than setting it invisibly.
    const effectiveVisitorType = draft.visitorType ?? visitorType;
    if (
      draft.type &&
      inviteTypesFor(orgType, effectiveVisitorType).includes(draft.type)
    )
      setType(draft.type);
    // Independent invitations carry their own dates, so the draft's window
    // fills every row; one visit keeps it in the settings.
    if (
      isOffice &&
      !bookingId &&
      shapeChoice === "separate" &&
      draft.guests.length >= 2
    ) {
      const window = {
        validFrom: draft.validFrom
          ? wallClockOf(new Date(draft.validFrom))
          : null,
        validTo: draft.validTo ? wallClockOf(new Date(draft.validTo)) : null,
      };
      if (window.validFrom || window.validTo)
        setRows((prev) => fillRowWindows(prev, window, "all"));
    } else {
      if (draft.validFrom) setStart(new Date(draft.validFrom));
      if (draft.validTo) setEnd(new Date(draft.validTo));
    }
    setDraftWarnings(warnings);
    return true;
  }

  const create = useMutation(
    orpc.invite.create.mutationOptions({
      onSuccess: async (createdInvite) => {
        if (bookingId) {
          toast.success("Invitations envoyées");
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: orpc.invite.list.key() }),
            queryClient.invalidateQueries({ queryKey: orpc.booking.key() }),
          ]);
          setShared({
            title: "Visiteurs invités",
            subtitle: bookingLocked?.chip ?? "",
            guests: createdInvite.guests,
            back: listBack,
            secondaryBack: {
              label: "Retour à mes réservations",
              to: "/reservations/mes-reservations",
            },
          });
          return;
        }
        toast.success("Invitation créée.");
        await queryClient.invalidateQueries({
          queryKey: orpc.invite.list.key(),
        });
        const selfFillCount = createdInvite.guests.filter(
          (g) => g.selfFill,
        ).length;
        if (
          afterGroupedCreate(createdInvite.guests.length, selfFillCount) ===
            "pass" &&
          createdInvite.guests[0]?.status === "active"
        ) {
          await navigate({
            to: "/pass/$token",
            params: { token: createdInvite.token },
          });
        } else {
          // Several guests, a self-fill guest or a pending one → share panel
          // in place. A self-fill guest's /pass/$token is the VISITOR's
          // form; a pending guest's opens nothing until approval.
          setShared({
            title:
              createdInvite.guests.length > 1
                ? "Visite créée"
                : "Invitation créée",
            subtitle: visitorsLine(included),
            guests: createdInvite.guests,
            back: listBack,
          });
        }
      },
      onError: (err) => {
        // Only vetted French reaches the toast — mirrors the discipline
        // `createMany` already applies server-side (see
        // `frenchErrorMessage`'s doc comment). The grouped path has its own
        // gate (`errors.length === 0`) same as separate, but the server's
        // `z.string().email()` is still stricter than this form's own
        // client-side check, so a bad contact can still reach here.
        console.error("[invite.create] failed", err);
        toast.error(
          inviteErrorMessage(err, "Impossible de créer l'invitation."),
        );
        // A form published (or changed) since this page read it: re-read,
        // so the form the server wants appears under the visitor's row.
        if (isFormRefusal(err)) refetchActiveForm();
      },
    }),
  );

  // The client cannot know what landed on a network error — never
  // auto-retry (matches the old /inviter/import behavior).
  const createMany = useMutation(
    orpc.invite.createMany.mutationOptions({
      onError: (err) => {
        console.error("[invite.createMany] failed", err);
        toast.error(
          inviteErrorMessage(
            err,
            "Erreur réseau. Vérifiez la liste des invitations avant de réessayer.",
          ),
        );
      },
    }),
  );

  // Leaving with visitors typed but not created loses them silently. The
  // v1 import route guarded this (spec 2026-08-27 section 6.3) and the guard
  // was dropped when that route folded into this page; this restores it for
  // all three input modes, not just the imported one.
  //
  // Keyed on CONTENT, not on `rows.length`: the page always seeds one empty
  // row, so a length check would prompt on a page nobody has touched. After a
  // fully successful `createMany` the re-index empties `rows`; after a partial
  // failure it leaves exactly the uncreated ones, which is precisely the
  // "invitations restantes" this asks about.
  //
  // `window.confirm` is this repo's existing confirm pattern - @gately/ui has
  // no AlertDialog.
  useBlocker({
    shouldBlockFn: () =>
      !window.confirm("Quitter sans créer les invitations restantes ?"),
    disabled:
      shared !== null ||
      create.isPending ||
      createMany.isPending ||
      !hasUnsavedVisitors(rows),
  });

  // A pending guest email flips to "sent" server-side asynchronously; poll
  // invite.list a few times, a few seconds apart, to reflect it without a
  // full page refresh. Bounded by a per-panel attempt counter so a guest
  // whose email never sends (e.g. delivery failure) doesn't poll forever —
  // after the attempts are exhausted the "sending" chip is left as-is. The
  // panel may span several invites (separate shape), so it is keyed on its
  // guest ids and matched across every listed row.
  const emailPollGuestIds = useRef<string | null>(null);
  const emailPollAttempts = useRef(0);

  useEffect(() => {
    if (!shared) return;
    const pending = shared.guests.filter(awaitsEmailSent);
    if (pending.length === 0) return;

    const key = shared.guests.map((g) => g.id).join(",");
    if (emailPollGuestIds.current !== key) {
      emailPollGuestIds.current = key;
      emailPollAttempts.current = 0;
    }
    if (emailPollAttempts.current >= MAX_EMAIL_POLL_ATTEMPTS) return;

    const timer = setTimeout(() => {
      emailPollAttempts.current += 1;
      void orpcClient.invite
        .list({ limit: 100 })
        .then((list) => {
          const fresh = new Map<
            string,
            { emailSentAt: string | Date | null; status: string }
          >();
          for (const row of list)
            for (const g of row.guests)
              fresh.set(g.id, { emailSentAt: g.emailSentAt, status: g.status });
          setShared((prev) =>
            prev
              ? {
                  ...prev,
                  guests: prev.guests.map((g) => {
                    const match = fresh.get(g.id);
                    return match ? { ...g, ...match } : g;
                  }),
                }
              : prev,
          );
        })
        .catch(() => {
          // Best-effort refresh only — leave the "sending" chip as-is.
        });
    }, 3000);
    return () => clearTimeout(timer);
  }, [shared]);

  const selectedUnit = units?.find((u) => u.id === unitId);
  // Same label as the picker: company name in an office, owner-prefixed unit
  // in a residence (see `unitOptionLabel`).
  const selectedUnitLabel = selectedUnit
    ? unitOptionLabel(selectedUnit, isOffice)
    : "";

  const tz = orgTimeZone ?? "Africa/Casablanca";
  // The rows that will be sent: not excluded, not blank — each with its
  // place in `rows` (`canGroup`'s offender indexes `included`).
  const includedEntries = rows.flatMap((r, i) =>
    !excluded.has(i) && !isBlankRow(r) ? [{ r, i }] : [],
  );
  const included = includedEntries.map(({ r }) => r);
  // Below 2 visitors there is no choice: always one invitation (audit P1-11).
  // A booking invite is always one grouped pass: `createMany` refuses it.
  const shape: InviteShape =
    bookingId || included.length < 2 ? "grouped" : shapeChoice;
  const separate = shape === "separate";
  // A booking's window is the server's for every guest: only the cap can refuse it.
  const groupCheck =
    included.length >= 2
      ? canGroup(
          bookingId
            ? included.map((r) => ({ ...r, validFrom: null, validTo: null }))
            : included,
          tz,
        )
      : null;
  const includedWithErrors = included.filter((r) => r.errors.length > 0);
  const windowIssues = new Map<number, WindowIssue>();
  if (separate)
    for (const { r, i } of includedEntries) {
      const issue = separateWindowIssue(r, type, tz);
      if (issue) windowIssues.set(i, issue);
    }
  const blocker =
    formBlocker ??
    submitBlocker({
      rows: included,
      validate: validateAnswers,
      separate,
      type,
      timeZone: tz,
    });
  const selfFillLine = selfFillNotice(included);
  // « Tous complètent eux-mêmes » leaves rows without an e-mail to the host:
  // counted next to the button (spec §3), before anyone clicks.
  const bulkSkipped = bulkSelfFill(included).skipped;
  const formsLine = formsToCompleteLine(included, validateAnswers);
  // Two rows or more: each form opens under its row. One: its own card.
  const inlineForm = rows.length >= 2;
  // The row the single-form card edits (always index 0), or null.
  const singleFormRow =
    !inlineForm &&
    validateAnswers &&
    rows[0] &&
    !excluded.has(0) &&
    !rows[0].selfFill
      ? rows[0]
      : null;
  const nonBlankCount = rows.filter((r) => !isBlankRow(r)).length;

  // « Aligner … sur les autres »: the row `canGroup` names takes the window
  // of the first dated row (the one it was compared against).
  const offender = groupCheck?.offender ?? null;
  const offenderEntry =
    offender !== null ? includedEntries[offender] : undefined;
  const alignLabel = offenderEntry
    ? `Aligner ${rowDisplayName(offenderEntry.r) || `la ligne ${offenderEntry.i + 1}`} sur les autres`
    : null;
  const alignTarget = included.find(
    (r) => r.validFrom !== null || r.validTo !== null,
  );
  const onAlign =
    offenderEntry && alignTarget
      ? () => {
          const window = {
            validFrom: alignTarget.validFrom,
            validTo: alignTarget.validTo,
          };
          setRows((prev) =>
            prev.map((r, i) =>
              i === offenderEntry.i
                ? (fillRowWindows([r], window, "all")[0] ?? r)
                : r,
            ),
          );
        }
      : null;

  // End must come after start. Guards the same-day "21:06 → 12:06" case the
  // calendar's date-only minDate can't catch (it constrains the day, not the
  // time within it). Mirrors the backend `inviteCreateInput` refine. Separate
  // mode hides these dates — each row carries its own (`windowIssues`).
  const invalidRange =
    !separate && !!start && !!end && end.getTime() <= start.getTime();
  // Half-filled: exactly one of the pair is set. The popover presents them
  // as one pair with no affordance saying "blank means midnight", so this is
  // far more likely a half-finished interaction than a deliberate choice —
  // and left alone it would silently default the missing bound to
  // 0/1439 server-side, so the created pass enforces hours the form never
  // showed. Mirrors the backend `inviteCreateInput` refine.
  const dailyWindowHalfFilled =
    type === "recurring" && (dayStart === "") !== (dayEnd === "");
  // A zero-length daily window admits nobody. Mirrors the backend
  // `inviteCreateInput` refine (now pathed to `dailyEndMinutes`) — without
  // this the same mistake as `invalidRange` above, a silent round-trip to
  // the server just to be told no, only for hours instead of dates.
  const dailyWindowZeroLength =
    type === "recurring" &&
    dayStart !== "" &&
    dayEnd !== "" &&
    hhmmToMinutes(dayStart) === hhmmToMinutes(dayEnd);
  const invalidDailyWindow = dailyWindowHalfFilled || dailyWindowZeroLength;

  // The settings window, built the same way `handleSubmit` sends it. In
  // « Une visite » it is the visit's only window; switching to independent
  // invitations copies it into the rows (`changeShape`).
  const sharedWindow = buildValidity({
    type,
    start,
    end,
    dayStart,
    dayEnd,
    weekdays: [...weekdays],
  });

  /** « Une visite » has ONE date source, the settings window: the included
   *  rows' common window (if any) MOVES there — the settings start/end take
   *  it (an absent end clears the settings end) and those rows' own dates are
   *  cleared, so nothing sits hidden in a column grouped mode does not show.
   *  After it, `commonRowWindow` finds no dated row, so the render-time
   *  adjustment below cannot loop. */
  function liftRowWindows() {
    const common = commonRowWindow(included, tz);
    if (!common) return;
    if (common.validFrom) setStart(new Date(common.validFrom));
    setEnd(common.validTo ? new Date(common.validTo) : undefined);
    const dated = new Set(
      includedEntries
        .filter(({ r }) => r.validFrom !== null || r.validTo !== null)
        .map(({ i }) => i),
    );
    setRows((prev) =>
      prev.map((r, i) =>
        dated.has(i)
          ? (fillRowWindows(
              [r],
              { validFrom: null, validTo: null },
              "all",
            )[0] ?? r)
          : r,
      ),
    );
  }

  // The pickers' wall clocks — what `liftRowWindows` writes into them.
  const settingsWindow = {
    validFrom: start ? wallClockOf(start) : null,
    validTo: end ? wallClockOf(end) : null,
  };
  // The visit already has another date: lifting would silently overwrite it
  // (a dated row re-included, say), so the author chooses instead.
  const liftConflict =
    !bookingId && shape === "grouped"
      ? settingsConflict(included, settingsWindow, tz)
      : null;
  const conflictEntry =
    liftConflict?.offender != null
      ? includedEntries[liftConflict.offender]
      : undefined;
  /** « Utiliser la date de la visite »: the named row drops its own dates. */
  function applyVisitDate() {
    if (!conflictEntry) return;
    setRows((prev) =>
      prev.map((r, i) =>
        i === conflictEntry.i
          ? (fillRowWindows(
              [r],
              { validFrom: null, validTo: null },
              "all",
            )[0] ?? r)
          : r,
      ),
    );
  }

  // An import or paste landing in « Une visite », a row excluded or aligned:
  // whenever grouped rows agree on a window, it moves into the settings —
  // when the settings have no start yet, or already hold that same window.
  // Adjusted during render (React's "state from state" pattern, not an
  // effect): guarded, and after the move `commonRowWindow` finds no dated
  // row, so it settles in one extra pass.
  if (
    !bookingId &&
    shape === "grouped" &&
    !liftConflict &&
    commonRowWindow(included, tz)
  ) {
    liftRowWindows();
  }

  // « Une visite » shows no per-row dates, so an import's date verdict there
  // would block the submit from a column nobody can see (`dropDateErrors`).
  // Same guarded render-time adjustment: once dropped, nothing matches.
  const dateErrorRows = separate
    ? []
    : includedEntries.filter(({ r }) => hasDateErrors(r)).map(({ i }) => i);
  if (dateErrorRows.length > 0) {
    const drop = new Set(dateErrorRows);
    setRows((prev) =>
      prev.map((r, i) => (drop.has(i) ? dropDateErrors(r) : r)),
    );
  }

  // Fewer than 2 visitors is always one visit: drop a separate choice so it
  // never resurfaces when a second row returns (the lift above then moves
  // the remaining row's dates, or names the conflict with the visit's).
  if (included.length < 2 && shapeChoice === "separate") setShape("grouped");

  /** « Tous complètent eux-mêmes » over the included rows only; a flipped
   *  row's last server failure no longer applies (as `handleSelfFillChange`). */
  function handleBulkSelfFill() {
    const targets = new Set(includedEntries.map(({ i }) => i));
    const { skipped } = bulkSelfFill(included);
    const flipped = includedEntries
      .filter(({ r }) => !r.selfFill && (r.email ?? "").trim() !== "")
      .map(({ i }) => i);
    setRows((prev) => {
      const out = bulkSelfFill(prev.filter((_, i) => targets.has(i))).rows;
      let k = 0;
      return prev.map((r, i) => (targets.has(i) ? (out[k++] ?? r) : r));
    });
    if (flipped.some((i) => failedReasons.has(i))) {
      setFailedReasons((prev) => {
        const next = new Map(prev);
        for (const i of flipped) next.delete(i);
        return next;
      });
    }
    if (skipped > 0)
      toast(`${skipped} visiteur(s) sans e-mail restent à compléter par vous.`);
  }

  /** Switching shape moves the dates to their new home (spec 2026-09-26 §3):
   *  to « Une visite » the rows' common window (if any) moves into the
   *  settings (`liftRowWindows`); to independent invitations the settings window fills every
   *  row whose start is empty. The pickers hold wall clocks as local Dates,
   *  which `buildValidity` turns back into wall clocks. */
  function changeShape(next: InviteShape) {
    if (next === shape) return;
    if (next === "grouped") liftRowWindows();
    else if (sharedWindow.validFrom || sharedWindow.validTo) {
      setRows((prev) =>
        fillRowWindows(
          prev,
          {
            validFrom: sharedWindow.validFrom ?? null,
            validTo: sharedWindow.validTo ?? null,
          },
          "empty",
        ),
      );
    }
    setShape(next);
  }

  // Independent invitations carry their own dates: the preview shows the
  // first included row's window, read as the pickers' device-local Dates.
  const previewRow = separate ? included[0] : undefined;
  const asPickerDate = (v: string | null) =>
    v ? new Date(toWallClock(v, tz)) : undefined;
  const previewStart = previewRow ? asPickerDate(previewRow.validFrom) : start;
  const previewEnd = previewRow ? asPickerDate(previewRow.validTo) : end;

  // Grid indices of the rows sent by the last `createMany` call, in the order
  // sent — `createMany` reports `row` as an index into THAT array, not into
  // `rows`.
  const sentIndices = useRef<number[]>([]);

  function submitSeparate(targetIndices?: number[]) {
    if (!unitId) return;
    const send = rows
      .map((r, i) => ({ r, i }))
      .filter(
        ({ r, i }) =>
          (targetIndices ? targetIndices.includes(i) : true) &&
          !excluded.has(i) &&
          !isBlankRow(r) &&
          r.errors.length === 0,
      );
    if (send.length === 0) return;
    sentIndices.current = send.map(({ i }) => i);
    createMany.mutate(
      {
        // No window, note or answers here: each row carries its own. A
        // settings note (hidden in this mode) would otherwise reach every
        // row without one.
        shared: {
          unitId: unitId === RESIDENCE ? undefined : unitId,
          locationId: locationId || undefined,
          visitorType,
          type,
          recurrence: sharedWindow.recurrence,
          dailyStartMinutes: sharedWindow.dailyStartMinutes,
          dailyEndMinutes: sharedWindow.dailyEndMinutes,
        },
        rows: send.map(({ r }) => toBatchRowPayload(r, !!formSchema)),
      },
      {
        onSuccess: (res) => {
          // The actual renumbering is a pure function (`visitor-rows.ts`) so
          // it can be unit-tested — this route file only wires its inputs
          // (current state) to its outputs (next state). See that function's
          // doc comment for what it guarantees.
          const next = reindexAfterPartialFailure(
            rows,
            excluded,
            failedReasons,
            sentIndices.current,
            res,
          );
          setRows(next.rows);
          setExcluded(next.excluded);
          setFailedReasons(next.failedReasons);
          setOpenFormRow(null);

          setBatchResult({
            created: res.created.length,
            failed: res.failed.length,
          });
          void queryClient.invalidateQueries({
            queryKey: orpc.invite.list.key(),
          });
          // A row may have failed on a form published since this page read
          // it: re-read, so the retry shows it.
          if (res.failed.length > 0) refetchActiveForm();
          if (res.failed.length === 0) {
            toast.success(`${res.created.length} invitation(s) créée(s).`);
            // The panel shows once everything landed; a partial failure
            // keeps the rows and the retry above.
            setShared({
              title:
                res.created.length === 1
                  ? "1 invitation créée"
                  : `${res.created.length} invitations créées`,
              subtitle: "Des invitations indépendantes, un QR chacune.",
              guests: res.created.map((c) => ({
                id: c.guestId,
                guestName: c.guestName,
                guestEmail: c.guestEmail,
                guestPhone: c.guestPhone,
                code: c.code,
                token: c.token,
                emailSentAt: null,
                selfFill: c.selfFill,
                status: c.status,
              })),
              back: listBack,
            });
          }
        },
      },
    );
  }

  function submitGrouped() {
    if (!hasHost) return;
    // Mirrors `submitSeparate`'s own filter, so both paths are symmetric on
    // their own terms rather than depending on `handleSubmit`'s gate for
    // correctness.
    const send = rows.filter(
      (r, i) => !excluded.has(i) && !isBlankRow(r) && r.errors.length === 0,
    );
    const common = {
      visitorType,
      guests: send.map((r) => toGuestPayload(r, !!formSchema)),
      note: note.trim() || undefined,
    };
    if (bookingId) {
      // The server derives the window, unit and meeting point from the
      // booking and ignores any sent — so none are.
      create.mutate({ ...common, bookingId, type: "one_time" });
      return;
    }
    create.mutate({
      ...common,
      unitId: unitId === RESIDENCE ? undefined : unitId,
      locationId: locationId || undefined,
      type,
      validFrom: sharedWindow.validFrom,
      validTo: sharedWindow.validTo,
      recurrence: sharedWindow.recurrence,
      dailyStartMinutes: sharedWindow.dailyStartMinutes,
      dailyEndMinutes: sharedWindow.dailyEndMinutes,
    });
  }

  const uploadBlocker = uploadsInFlight > 0 ? UPLOAD_IN_FLIGHT_MESSAGE : null;
  const cannotSubmit =
    uploadBlocker !== null ||
    !hasHost ||
    (!bookingId && (invalidRange || invalidDailyWindow)) ||
    blocker !== null ||
    liftConflict !== null ||
    includedWithErrors.length > 0 ||
    (shape === "grouped" && !!groupCheck);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // The ref, not `cannotSubmit`: an upload may have started since render.
    if (cannotSubmit || uploadsInFlightRef.current > 0) return;
    if (shape === "grouped") submitGrouped();
    else submitSeparate();
  }

  if (shared) {
    return (
      <div className="px-7 py-6">
        <InviteSharePanel
          title={shared.title}
          subtitle={shared.subtitle}
          guests={shared.guests}
          primaryBack={shared.back}
          secondaryBack={shared.secondaryBack}
          onCreateAnother={() => {
            if (!resetForm()) return;
            setShared(null);
          }}
        />
      </div>
    );
  }

  // Staff and gardiens can never complete this form (no unit, no residence
  // scope) — say so instead of showing an empty picker with resident-oriented
  // advice. A unit-less proprietaire keeps the form: for them the "ask the
  // syndic" hint below the picker is correct and their state is transient.
  if (activeRole === "owner" || activeRole === "gardien") {
    return (
      <div className="px-7 py-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] text-neutral-950">
          Nouvelle invitation
        </h1>
        <p className="mt-2 max-w-md text-sm text-neutral-500">
          Les invitations sont créées par les {terms.adminPlural.toLowerCase()},
          ou par les {terms.ownerPlural.toLowerCase()} pour leurs propres{" "}
          {terms.unitPlural}. Votre rôle n'en crée pas.
        </p>
      </div>
    );
  }

  if (bookingUnusable) {
    return (
      <div className="px-7 py-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] text-neutral-950">
          Inviter des visiteurs
        </h1>
        <p role="alert" className="mt-2 max-w-md text-sm text-neutral-500">
          {bookingQuery.isError
            ? frenchErrorMessage(
                bookingQuery.error,
                "Impossible de charger la réservation.",
              )
            : "La réservation doit être confirmée pour inviter des visiteurs."}
        </p>
        <Link
          to="/reservations/mes-reservations"
          className="text-brand mt-4 inline-block text-sm font-medium hover:underline"
        >
          Retour à mes réservations
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 py-5 sm:px-7 sm:py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-[-0.02em] text-neutral-950">
          Nouvelle invitation
        </h1>
        <p className="mt-1 text-sm text-neutral-500">
          Chaque visiteur reçoit son propre QR code à présenter à l'accueil.
          Aucun compte requis.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          {/* ── LEFT: form cards ── */}
          <div className="flex min-w-0 flex-col gap-5">
            {/* Card 0: who is invited — picks the form and the pass types */}
            {visitorTypeOptions.length < 2 ? null : (
              <VisitorTypeCard
                options={visitorTypeOptions}
                value={visitorType}
                onChange={handleVisitorTypeChange}
              />
            )}

            {/* Card 1: Visitor list */}
            <div className="rounded-[14px] border border-neutral-200 bg-white p-4 sm:p-6">
              <div className="mb-4 text-[13.5px] font-semibold text-neutral-950">
                Visiteurs · {nonBlankCount}
              </div>

              <VisitorInputModes
                mode={mode}
                onModeChange={(next) =>
                  void setModeSearch({
                    search: (prev) => ({ ...prev, mode: next }),
                    replace: true,
                  })
                }
                isOffice={isOffice}
                hasLlm={hasLlm}
                allowImport={!bookingId}
                rows={rows}
                maxRows={MAX_ROWS}
                onAddRow={addRow}
                onReplaceRows={replaceRows}
                onApplyPasteDraft={applyPasteDraft}
                recentVisitors={recentVisitors}
                onPickVisitor={pickVisitor}
              />

              {draftWarnings.length > 0 ? (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <ul className="flex flex-col gap-1">
                    {draftWarnings.map((w, i) => (
                      <li key={i} className="text-[13px] text-amber-900">
                        {w.detail}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => setDraftWarnings([])}
                    className="mt-2 text-[12px] font-medium text-amber-900 underline"
                  >
                    Masquer
                  </button>
                </div>
              ) : null}

              {separate ? (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-[10px] border border-neutral-100 bg-neutral-50 px-3 py-2.5">
                  <span className="text-[13px] font-medium">
                    Même créneau pour toutes les lignes
                  </span>
                  <Input
                    type="datetime-local"
                    className="h-8 w-[190px]"
                    value={fillFrom}
                    onChange={(e) => setFillFrom(e.target.value)}
                    aria-label="Début à appliquer"
                  />
                  <Input
                    type="datetime-local"
                    className="h-8 w-[190px]"
                    value={fillTo}
                    onChange={(e) => setFillTo(e.target.value)}
                    aria-label="Fin à appliquer"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!fillFrom}
                    onClick={() =>
                      setRows((p) =>
                        fillRowWindows(
                          p,
                          {
                            validFrom: fillFrom || null,
                            validTo: fillTo || null,
                          },
                          "empty",
                        ),
                      )
                    }
                  >
                    Appliquer aux lignes vides
                  </Button>
                  <button
                    type="button"
                    className="text-brand text-[12.5px] font-medium disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={!fillFrom}
                    onClick={() =>
                      setRows((p) =>
                        fillRowWindows(
                          p,
                          {
                            validFrom: fillFrom || null,
                            validTo: fillTo || null,
                          },
                          "all",
                        ),
                      )
                    }
                  >
                    à toutes
                  </button>
                </div>
              ) : null}

              {selfFillLine ||
              (validateAnswers &&
                included.filter((r) => !r.selfFill).length >= 2) ? (
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[12.5px] text-neutral-500">
                    {selfFillLine}
                  </p>
                  {validateAnswers &&
                  included.filter((r) => !r.selfFill).length >= 2 ? (
                    <div className="flex flex-wrap items-center gap-2">
                      {bulkSkipped > 0 ? (
                        <span className="text-[12px] text-neutral-400">
                          {bulkSkipped} sans e-mail
                        </span>
                      ) : null}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleBulkSelfFill}
                      >
                        Tous complètent eux-mêmes
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <VisitorReviewTable
                rows={rows}
                excluded={excluded}
                failedReasons={failedReasons}
                grouped={!separate}
                timeZone={tz}
                dayEndHhmm={dayEndHhmm}
                onFieldChange={handleFieldChange}
                onToggle={toggleRow}
                onSelfFillChange={handleSelfFillChange}
                form={
                  formSchema && validateAnswers && answerErrors
                    ? {
                        schema: formSchema,
                        title: activeFormTitle,
                        uploadFile,
                        validate: validateAnswers,
                        errors: answerErrors,
                      }
                    : null
                }
                inlineForm={inlineForm}
                openFormRow={openFormRow}
                onToggleForm={(i) =>
                  setOpenFormRow((c) => (c === i ? null : i))
                }
                onFormAnswersChange={handleFormAnswersChange}
                onCompanionsChange={handleCompanionsChange}
                onDelete={deleteRow}
                deleteDisabled={uploadsInFlight > 0}
                windowIssues={windowIssues}
              />

              <InviteShapeControl
                rowCount={included.length}
                groupCheck={groupCheck}
                alignLabel={alignLabel}
                onAlign={onAlign}
                value={shape}
                onChange={changeShape}
                separateReason={
                  bookingId
                    ? "Une réservation donne une seule visite."
                    : isOffice
                      ? null
                      : "Réservé aux organisations de type bureau."
                }
              />

              {liftConflict ? (
                <p className="mb-4 text-[12.5px] text-(--danger-500)">
                  {liftConflict.reason}{" "}
                  <button
                    type="button"
                    className="text-brand font-medium underline"
                    onClick={applyVisitDate}
                  >
                    Utiliser la date de la visite
                  </button>
                </p>
              ) : null}

              {separate && batchResult ? (
                <ImportResult
                  created={batchResult.created}
                  failed={batchResult.failed}
                  busy={createMany.isPending}
                  onRetryFailed={() => {
                    // A retry re-submits by row index, like submit itself.
                    if (refuseWhileUploading()) return;
                    submitSeparate([...failedReasons.keys()]);
                  }}
                />
              ) : null}

              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-neutral-500">
                  {visitorsLine(included)}
                  {formsLine ? ` · ${formsLine}` : ""}
                  {includedWithErrors.length > 0
                    ? ` · ${includedWithErrors.length} ligne(s) en erreur`
                    : ""}
                </div>
                {includedWithErrors.length > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      setExcluded((prev) => {
                        const next = new Set(prev);
                        for (const [i, r] of rows.entries()) {
                          if (r.errors.length > 0) next.add(i);
                        }
                        return next;
                      })
                    }
                  >
                    Exclure les lignes en erreur
                  </Button>
                ) : null}
              </div>
            </div>

            {/* Card 1b: the one visitor's form (two rows or more: under each row) */}
            {singleFormRow && formSchema ? (
              <div className="rounded-[14px] border border-neutral-200 bg-white p-4 sm:p-6">
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-[13.5px] font-semibold text-neutral-950">
                    {activeFormTitle ?? "Formulaire"} —{" "}
                    {rowDisplayName(singleFormRow) || "le visiteur"}
                  </div>
                  {singleFormRow.email ? (
                    <button
                      type="button"
                      className="text-brand text-[12.5px] font-medium"
                      onClick={() => handleSelfFillChange(0, true)}
                    >
                      Laisser le visiteur le compléter lui-même →
                    </button>
                  ) : null}
                </div>
                <FormRenderer
                  schema={formSchema}
                  value={singleFormRow.formAnswers}
                  onChange={(a) => handleFormAnswersChange(0, a)}
                  errors={answerErrors?.(singleFormRow.formAnswers)}
                  uploadFile={uploadFile}
                />
              </div>
            ) : null}

            {/* Card 2: Access & validity */}
            <div className="rounded-[14px] border border-neutral-200 bg-white p-4 sm:p-6">
              <div className="mb-4 text-[13.5px] font-semibold text-neutral-950">
                Accès &amp; validité
              </div>

              {bookingId ? (
                <div className="mb-4 flex flex-col gap-1.5">
                  <Label>Horaires de la réservation</Label>
                  <div className="rounded-[10px] border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-[13.5px] font-medium text-neutral-950">
                    {bookingLocked?.chip ?? "Chargement…"}
                  </div>
                  <p className="text-[12px] text-neutral-400">
                    Le QR s'active 30 minutes avant le début de la réservation.
                  </p>
                </div>
              ) : null}

              <SharedInviteSettings
                data={settings}
                hideHostPickers={!!bookingId}
                terms={terms}
                isOffice={isOffice}
                isSyndic={isSyndic}
                unitId={unitId}
                onUnitIdChange={setUnitId}
                locationId={locationId}
                onLocationIdChange={setLocationId}
                // Independent invitations: each row's own note column.
                note={separate ? undefined : note}
                onNoteChange={separate ? undefined : setNote}
              />

              {bookingId ? null : (
                <PassWindowFields
                  terms={terms}
                  typeOptions={typeOptions}
                  type={type}
                  onTypeChange={setType}
                  start={start}
                  onStartChange={setStart}
                  end={end}
                  onEndChange={setEnd}
                  dayStart={dayStart}
                  onDayStartChange={setDayStart}
                  dayEnd={dayEnd}
                  onDayEndChange={setDayEnd}
                  weekdays={weekdays}
                  onWeekdaysChange={setWeekdays}
                  timeZone={orgTimeZone ?? ""}
                  dayEndHhmm={dayEndHhmm}
                  invalidRange={invalidRange}
                  dailyWindowHalfFilled={dailyWindowHalfFilled}
                  dailyWindowZeroLength={dailyWindowZeroLength}
                  hideDates={separate}
                />
              )}
            </div>

            {/* Footer actions */}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              {(uploadBlocker ?? blocker ?? liftConflict?.reason) ? (
                <span className="mr-auto text-[13px] text-[#9a3412]">
                  {uploadBlocker ?? blocker ?? liftConflict?.reason}
                </span>
              ) : null}
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  void navigate({
                    to: bookingId
                      ? "/reservations/mes-reservations"
                      : "/invitations",
                  })
                }
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={
                  cannotSubmit || create.isPending || createMany.isPending
                }
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.95"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" />
                </svg>
                {create.isPending || createMany.isPending
                  ? "Création…"
                  : separate
                    ? `Créer ${included.length} invitation${included.length > 1 ? "s" : ""}`
                    : included.length > 1
                      ? `Créer la visite · ${included.length} QR`
                      : "Créer l'invitation"}
              </Button>
            </div>
          </div>

          {/* ── RIGHT: sticky preview ── */}
          <PassPreview
            guestName={
              included[0]
                ? included[0].selfFill && !included[0].name.trim()
                  ? "Complété par le visiteur"
                  : rowDisplayName(included[0])
                : ""
            }
            peopleLabel={
              included[0] && included[0].companions > 0
                ? `${included[0].companions + 1} (vous + ${included[0].companions})`
                : "1 (seul)"
            }
            headline={
              separate
                ? `${included.length} invitations · 1 QR chacune`
                : included.length >= 2
                  ? `Une visite · ${included.length} QR`
                  : "Invitation"
            }
            unitLabel={
              bookingId ? (booking?.unitLabel ?? "") : selectedUnitLabel
            }
            unitFallback={terms.hostUnitLabel}
            type={bookingId ? "one_time" : type}
            typeLabel={typeLabelFor(bookingId ? "one_time" : type, terms)}
            validityLabel={bookingLocked?.chip}
            start={previewStart}
            end={previewEnd}
            chips={previewChips(included)}
            orgTimeZone={orgTimeZone}
            weekdaysLabel={
              weekdays.size
                ? WEEKDAYS.filter((w) => weekdays.has(w.code))
                    .map((w) => w.label)
                    .join(", ")
                : undefined
            }
            hoursLabel={
              orgTimeZone && dayStart && dayEnd
                ? formatDailyWindow(
                    hhmmToMinutes(dayStart),
                    hhmmToMinutes(dayEnd),
                    orgTimeZone,
                  )
                : undefined
            }
          />
        </div>
      </form>
    </div>
  );
}
